'use client';

import { useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAsyncEffect } from 'ahooks';
import { Skeleton } from '@mui/material';
import MarketDetail from '@/components/predic/detail/market/MarketDetail';
import type { PolymarketMarket } from '@/components/predic/detail/market/types';
import { MarketCard, type MarketCardMarket } from '../cards/market';
import { useRightDrawer } from '@/contexts/DrawerContext';
import customFetch from '@/lib/utils/fetch';
import type { MarketInfo, MarketRecommendation } from '@/type/chat';
import { cn } from '@/lib/utils';
import SwrLocalCacheProvider from '@/lib/swr/SwrLocalCacheProvider';
import { useMarketDrawerStore } from '@/stores/marketDrawerStore';

type MarketData = Partial<MarketInfo & PolymarketMarket> & {
  title?: string;
  category?: string;
  event_icon?: string | null;
  event_id?: string;
  slug?: string;
};

export default function AsyncMarketCard({
  recommendations,
  sourceId,
  subMarket,
}: {
  sourceId: string;
  subMarket?: string;
  recommendations?: MarketRecommendation[];
}) {
  const [marketData, setMarketData] = useState<MarketData>();
  const [loading, setLoading] = useState(false);
  const { openDrawer, setDrawerWidth } = useRightDrawer();
  const openMarketDrawer = useMarketDrawerStore((state) => state.openMarketDrawer);
  const pathname = usePathname();

  const eventSlug = marketData?.slug || marketData?.event_slug;
  const marketEventId = String(marketData?.event_id || '');

  const cardMarket = useMemo<MarketCardMarket | null>(() => {
    if (!marketData) return null;

    return {
      imageSrc: marketData.event_icon || marketData.market_icon || marketData.market_image || '',
      imageAlt: marketData.title || marketData.market_question || '',
      title: marketData.title || marketData.market_question || '',
      outcome: marketData.group_item_title || marketData.outcome_yes || 'YES',
      probability: toNumber(marketData.price || marketData.outcome_yes_price) * 100,
      change: toNumber(marketData.price_change_24h) * 100,
      changePeriod: '(24h)',
      volume: toNumber(marketData.volume || marketData.total_volume_usdc),
      category: marketData.category || '',
      resolvedOutcome:
        marketData.market_closed === 1
          ? toNumber(marketData.price || marketData.outcome_yes_price) > 0.5
            ? 'YES'
            : 'NO'
          : undefined,
    };
  }, [marketData]);

  function handleMarket() {
    if (!eventSlug || loading) return;

    // 抽屉内容渲染在根布局（predic layout 之外），需自带缓存 Provider
    const detail = (
      <SwrLocalCacheProvider>
        <MarketDetail id={eventSlug} openInSidebar />
      </SwrLocalCacheProvider>
    );

    // 会话页之外（实体页、新闻页等）卡片是在 FixedChatWindow 的全屏浮层里，
    // 根布局的全局抽屉会被浮层盖住，改用浮层内部的抽屉展示。
    if (!pathname?.startsWith('/predic/c/')) {
      openMarketDrawer(detail, 600);
      return;
    }

    setDrawerWidth(600);
    openDrawer(detail);
  }

  useAsyncEffect(async () => {
    const recommendation = recommendations?.find(
      (item: MarketRecommendation) => item.market.uid === sourceId,
    );

    if (recommendation) {
      setMarketData(recommendation.market as unknown as MarketData);
      return;
    }

    if (!sourceId.startsWith('slug:') && !sourceId.startsWith('slug/')) {
      setMarketData(undefined);
      return;
    }

    try {
      setLoading(true);
      const eventSlug = sourceId.slice(5);
      const params = new URLSearchParams({ event_slug: eventSlug });

      if (subMarket && subMarket !== eventSlug) {
        params.set('sub_market', subMarket);
      }

      const resp = await customFetch(`/api/market/basic?${params.toString()}`);
      if (!(resp instanceof Response)) {
        setMarketData(undefined);
        return;
      }

      const res = await resp.json();
      const event = res?.data?.event;
      const market = res?.data?.markets?.[0];

      if (!event || !market) {
        setMarketData(undefined);
        return;
      }

      setMarketData({
        ...market,
        volume: event.volume_24hr,
        title: event.title,
        category: event.category,
        event_icon: event.icon || event.image,
        event_id: String(event.id || market.event_id || ''),
        slug: event.slug,
      });
    } finally {
      setLoading(false);
    }
  }, [sourceId, subMarket, recommendations]);

  if (loading) {
    return (
      <div
        className={cn(
          'rounded-xl overflow-hidden border border-[#1E1E1E] bg-[#121212] max-w-97.5',
          'p-4 my-5 space-y-3',
        )}
      >
        <div className="flex items-center gap-2.5">
          <Skeleton variant="rounded" width={44} height={44} sx={{ bgcolor: 'grey.800' }} />
          <div className="flex-1">
            <Skeleton variant="text" width="80%" sx={{ fontSize: '1rem', bgcolor: 'grey.800' }} />
            <Skeleton variant="text" width="60%" sx={{ fontSize: '1rem', bgcolor: 'grey.800' }} />
          </div>
        </div>
        <Skeleton variant="text" width="80%" sx={{ fontSize: '0.75rem', bgcolor: 'grey.800' }} />
      </div>
    );
  }

  if (!cardMarket || !marketEventId) {
    return null;
  }

  return (
    <div className="my-5 max-w-97.5 cursor-pointer" onClick={handleMarket}>
      <MarketCard
        marketEventId={marketEventId}
        market={cardMarket}
        href={eventSlug ? `/predic/market?id=${eventSlug}` : undefined}
        linkEnabled={false}
      />
    </div>
  );
}

function toNumber(value?: string | number | null) {
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? 0 : numberValue;
}
